import {Html} from '@react-three/drei';
import {Canvas} from '@react-three/fiber';
import {XR, XRSpace, useXR} from '@react-three/xr';
import {Suspense, useEffect, useState} from 'react';

import {WorkoutSequence} from '../components/app/WorkoutSequence';
import {VRPreviewModal} from '../components/modals/VRPreviewModal';
import {VR_POSITIONS} from '../constants/vrPositions';
import {ballPositionStore} from '../hooks/useXRController';
import {useAuthStore} from '../store/authStore';
import {useStore} from '../store/useStore';
import {xrStore} from '../store/xrStore';
import {ExerciseSettings, TrainingMode} from '../types/exercises';
import {recordSession} from '../utils/firebase';
import {logger} from '../utils/logger';

interface VRSceneProps {
  children?: React.ReactNode;
  onClose: (finalBallPosition?: {x: number; y: number; z: number}, sessionCompleted?: boolean) => void;
  showPreview: boolean;
  onHidePreview: () => void;
  exerciseSequence: {type: TrainingMode; duration: number; settings?: ExerciseSettings}[];
}

// Component to handle XR session setup
const XRSessionHandler = ({onHidePreview}: {onHidePreview: () => void}) => {
  const {session, origin} = useXR(state => ({
    session: state.session,
    origin: state.origin,
  }));

  useEffect(() => {
    logger.info('XRSessionHandler effect triggered:', {
      sessionActive: !!session,
      sessionState: session?.visibilityState,
      hasOrigin: !!origin,
    });

    const setupSession = async () => {
      if (!session) return;

      try {
        // Set up input source handling
        session.addEventListener('inputsourceschange', (event: XRInputSourcesChangeEvent) => {
          logger.info('Input sources changed:', {
            added: event.added.length,
            removed: event.removed.length,
          });
        });

        onHidePreview();
      } catch (error) {
        logger.error('Error setting up XR session:', error);
      }
    };

    setupSession();
  }, [session, origin, onHidePreview]);

  return null;
};

// Custom hook for VR initialization
const useVRInitialization = (isDevMode: boolean) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    logger.info('VRScene initialization check starting:', {isDevMode});

    if (isDevMode) {
      setIsInitialized(true);
      return;
    }

    // Check WebXR support
    logger.info('Checking WebXR support:', {
      hasNavigatorXR: !!navigator.xr,
      userAgent: navigator.userAgent,
      isSecureContext: window.isSecureContext,
    });

    if (!window.isSecureContext) {
      const errorMsg = 'WebXR requires a secure context (HTTPS)';
      logger.error(errorMsg);
      setError(errorMsg);
      return;
    }

    if (!navigator.xr) {
      const errorMsg = 'WebXR is not supported in this browser';
      logger.error(errorMsg);
      setError(errorMsg);
      return;
    }

    // Check if immersive-vr is supported
    navigator.xr
      .isSessionSupported('immersive-vr')
      .then(supported => {
        logger.info('Immersive VR support result:', {supported});
        if (!supported) {
          const errorMsg = 'Immersive VR is not supported on this device';
          logger.error(errorMsg);
          setError(errorMsg);
        } else {
          logger.info('VR is supported, initializing...');
          setIsInitialized(true);
        }
      })
      .catch((error: Error) => {
        const errorMsg = `Error checking VR support: ${error.message}`;
        logger.error('VR support check failed:', error);
        setError(errorMsg);
      });
  }, [isDevMode]);

  return {isInitialized, error};
};

// Custom hook for workout completion
const useWorkoutCompletion = (
  exerciseSequence: {type: TrainingMode; duration: number; settings?: ExerciseSettings}[],
  onClose: (finalBallPosition?: {x: number; y: number; z: number}, sessionCompleted?: boolean) => void
) => {
  const [workoutStartTime] = useState(() => Date.now());

  const handleWorkoutComplete = async () => {
    try {
      logger.info('handleWorkoutComplete called');

      // End the VR session
      const session = xrStore.getState().session;
      if (session) {
        logger.info('Ending VR session');
        await session.end();
        logger.info('VR session ended successfully');
      } else {
        logger.info('No active VR session to end');
      }

      logger.info('VRScene handleWorkoutComplete', {exerciseSequence});
      // Record session metrics
      const {user} = useAuthStore.getState();
      logger.info('Current user:', user);

      if (user?.uid) {
        // Check if any exercise has unlimited duration
        const hasUnlimitedDuration = exerciseSequence.some(exercise => exercise.duration === -1);

        // Calculate total duration
        let totalDuration: number;
        if (hasUnlimitedDuration) {
          // Use actual elapsed time for unlimited sessions
          totalDuration = Math.floor((Date.now() - workoutStartTime) / 1000);
        } else {
          // Sum up preset durations for timed sessions
          totalDuration = exerciseSequence.reduce((acc, exercise) => acc + exercise.duration, 0);
        }

        logger.info('Recording session with metrics:', {
          totalDuration,
          exerciseCount: exerciseSequence.length,
          hasUnlimitedDuration,
          exercises: exerciseSequence.map(exercise => ({
            type: exercise.type,
            duration: exercise.duration,
          })),
        });

        try {
          await recordSession(user.uid, {
            duration: totalDuration,
            exercises: exerciseSequence.map(exercise => ({
              type: exercise.type,
              // Record actual elapsed time for unlimited exercises
              duration: exercise.duration === -1 ? totalDuration : exercise.duration,
            })),
          });
          logger.info('Session recorded successfully');
        } catch (recordError) {
          logger.error('Error recording session metrics:', recordError);
        }
      } else {
        logger.info('No user ID available to record session');
      }

      // Get final ball position from global store and pass it back
      const finalBallPosition = ballPositionStore.getOffset();
      logger.info('Final ball position:', finalBallPosition);
      onClose(finalBallPosition, true);
    } catch (error) {
      logger.error('Error in handleWorkoutComplete:', error);
      // Still close the scene even if there's an error ending the session
      onClose(undefined, true);
    }
  };

  return {handleWorkoutComplete};
};

export const VRScene: React.FC<VRSceneProps> = ({children, onClose, showPreview, exerciseSequence}) => {
  const {isDevMode, eyeToTrain} = useStore();
  const {isInitialized, error} = useVRInitialization(isDevMode);
  const {handleWorkoutComplete} = useWorkoutCompletion(exerciseSequence, onClose);
  const [showWorkout, setShowWorkout] = useState(false);

  if (error && !isDevMode) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          backdropFilter: 'blur(4px)',
        }}
      >
        <div
          style={{
            background: '#1e1e1e',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.6)',
          }}
        >
          <h3
            style={{
              fontSize: '1.1rem',
              fontWeight: '600',
              color: '#ffffff',
              margin: '0 0 0.5rem 0',
            }}
          >
            VR Not Available
          </h3>
          <p
            style={{
              fontSize: '0.85rem',
              color: 'rgba(255, 255, 255, 0.7)',
              margin: '0 0 1.5rem 0',
              lineHeight: '1.6',
            }}
          >
            {error}
          </p>
          <div style={{display: 'flex', gap: '0.75rem'}}>
            <button
              onClick={() => onClose(undefined, false)}
              style={{
                flex: 1,
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'transparent',
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.85rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              onMouseDown={e => {
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showPreview) {
    const settings = exerciseSequence[0]?.settings;
    const settingsKey = settings
      ? JSON.stringify({
          leftEyeColor: settings.leftEyeColor,
          rightEyeColor: settings.rightEyeColor,
          leftEyeOverlay: settings.leftEyeOverlay,
          rightEyeOverlay: settings.rightEyeOverlay,
          speed: settings.speed,
          range: settings.range,
        })
      : '';

    return (
      <VRPreviewModal
        isInitialized={isInitialized}
        onClose={onClose}
        eyeToTrain={exerciseSequence[0]?.settings?.eyeToTrain || eyeToTrain}
        currentExercise={exerciseSequence[0]?.type || 'convergence'}
        settingsKey={settingsKey}
      >
        {children || (
          <WorkoutSequence
            exercises={exerciseSequence}
            onComplete={handleWorkoutComplete}
            onEndExercise={handleWorkoutComplete}
          />
        )}
      </VRPreviewModal>
    );
  }

  return (
    <Canvas
      gl={{
        antialias: true,
        alpha: true,
        preserveDrawingBuffer: true,
        powerPreference: 'high-performance',
      }}
      style={{background: '#000000'}}
      dpr={[1, 2]}
      frameloop="always"
      onCreated={({gl, camera}) => {
        logger.info('Canvas created, ready for XR', {
          isWebGL2: gl.capabilities.isWebGL2,
          maxTextures: gl.capabilities.maxTextures,
          precision: gl.capabilities.precision,
        });

        camera.updateMatrixWorld();

        if (gl.xr) {
          gl.xr.enabled = true;
        }

        // Add context loss handling
        gl.domElement.addEventListener('webglcontextlost', event => {
          event.preventDefault();
          logger.error('WebGL context lost');
        });

        gl.domElement.addEventListener('webglcontextrestored', () => {
          logger.info('WebGL context restored');
          gl.xr.enabled = true;
          camera.updateMatrixWorld();
        });
      }}
    >
      <XR store={xrStore}>
        <XRSessionHandler onHidePreview={() => setShowWorkout(true)} />
        <Suspense
          fallback={
            <Html center>
              <div style={{color: 'white', fontSize: '24px'}}>Loading...</div>
            </Html>
          }
        >
          <ambientLight intensity={0.1} />
          <directionalLight position={VR_POSITIONS.LIGHTS.DIRECTIONAL} intensity={0.5} />

          {/* Main content - position at eye level */}
          <XRSpace space="viewer">
            {showWorkout && (
              <WorkoutSequence
                exercises={exerciseSequence}
                onComplete={handleWorkoutComplete}
                onEndExercise={handleWorkoutComplete}
              />
            )}
          </XRSpace>

          {/* Debug info */}
          <Html position={[0, -2, -2]} center>
            <div style={{color: 'white', fontSize: '16px', textAlign: 'center'}}>
              {isDevMode ? 'Dev Mode: Active' : `VR Mode: ${isInitialized ? 'Initialized' : 'Initializing...'}`}
            </div>
          </Html>
        </Suspense>
      </XR>
    </Canvas>
  );
};
